import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Cpu, Headphones, Cloud, MailCheck, Loader2, Droplets } from "lucide-react";
import logo from "@/assets/dosatronic-logo.png";

const BG = "https://images.unsplash.com/photo-1705708551758-76b153fa536e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHx3YXRlciUyMHRyZWF0bWVudCUyMHBsYW50JTIwbW9kZXJufGVufDB8fHx8MTc4Mzg0MzI1Mnww&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(user ? "/" : "/login");
  };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const values = [
    { icon: Cpu, title: t("about.v1Title"), body: t("about.v1Body") },
    { icon: Headphones, title: t("about.v2Title"), body: t("about.v2Body") },
    { icon: Cloud, title: t("about.v3Title"), body: t("about.v3Body") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name, email, company, message, lang });
      setSent(true);
      setName(""); setEmail(""); setCompany(""); setMessage("");
      toast.success(t("about.sent"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("about.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo title={`${t("about.title")} — Dosacloud | Dosatronic`} description={t("about.subtitle")} path="/sobre" />

      <header className="flex items-center justify-between px-6 py-4 sm:px-10">
        <Link to={user ? "/" : "/login"} className="inline-flex items-center rounded-md bg-white px-2.5 py-2">
          <img src={logo} alt="Dosatronic" className="h-8 w-auto" data-testid="about-logo" />
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Button asChild variant="ghost" size="sm" data-testid="about-back">
            <button type="button" onClick={goBack}><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("about.backHome")}</button>
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-slate-900/85 to-blue-800/80" />
        <div className="relative mx-auto max-w-4xl px-6 py-20 text-white sm:px-10 sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] backdrop-blur">
            <Droplets className="h-3.5 w-3.5" /> Dosatronic
          </span>
          <h1 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">{t("about.title")}</h1>
          <p className="mt-4 max-w-xl text-lg text-blue-100">{t("about.tagline")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16 sm:px-10">
        <h2 className="text-lg font-semibold text-slate-900">{t("about.subtitle")}</h2>
        <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
          <p>{t("about.p1")}</p>
          <p>{t("about.p2")}</p>
          <p>{t("about.p3")}</p>
          <p>{t("about.p4")}</p>
          <p>{t("about.p5")}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {values.map((v) => (
            <Card key={v.title} className="border-slate-200 p-6 shadow-none">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <v.icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-slate-800">{v.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{v.body}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-2xl px-6 py-16 pb-32 sm:px-10">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{t("about.contactTitle")}</h2>
          <p className="mt-2 text-sm text-slate-500">{t("about.contactSubtitle")}</p>

          {sent ? (
            <div data-testid="contact-sent" className="mt-8 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              <MailCheck className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.75} />
              <span>{t("about.sent")}</span>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("about.formName")}</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("about.formEmail")}</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="contact-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("about.formCompany")}</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} className="mt-1.5" data-testid="contact-company" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("about.formMessage")}</Label>
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={5}
                  placeholder={t("about.messagePlaceholder")} className="mt-1.5" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={busy} data-testid="contact-submit" className="bg-blue-600 text-white hover:bg-blue-700">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {busy ? t("about.sending") : t("about.send")}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
